import { lazy, Suspense, type ComponentType } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { Layout, Protected } from "@/components/Layout";

const lazyNamed = <T extends Record<string, unknown>, K extends keyof T>(
  loader: () => Promise<T>,
  name: K,
) =>
  lazy(async () => ({ default: (await loader())[name] as ComponentType<any> }));
const Home = lazyNamed(() => import("@/pages/public"), "Home");
const Marketplace = lazyNamed(() => import("@/pages/public"), "Marketplace");
const Categories = lazyNamed(() => import("@/pages/public"), "Categories");
const CategoryPage = lazyNamed(() => import("@/pages/public"), "CategoryPage");
const SellerPage = lazyNamed(() => import("@/pages/public"), "SellerPage");
const Login = lazyNamed(() => import("@/pages/auth"), "Login");
const Register = lazyNamed(() => import("@/pages/auth"), "Register");
const StaffLogin = lazyNamed(() => import("@/pages/auth"), "StaffLogin");
const ProductPage = lazyNamed(() => import("@/pages/commerce"), "ProductPage");
const NewListing = lazyNamed(() => import("@/pages/commerce"), "NewListing");
const PaymentResult = lazyNamed(
  () => import("@/pages/commerce"),
  "PaymentResult",
);
const Dashboard = lazyNamed(() => import("@/pages/account"), "Dashboard");
const Wallet = lazyNamed(() => import("@/pages/account"), "Wallet");
const Purchases = lazyNamed(() => import("@/pages/account"), "Purchases");
const Sales = lazyNamed(() => import("@/pages/account"), "Sales");
const Notifications = lazyNamed(
  () => import("@/pages/account"),
  "Notifications",
);
const Profile = lazyNamed(() => import("@/pages/account"), "Profile");
const Settings = lazyNamed(() => import("@/pages/account"), "Settings");
const Support = lazyNamed(() => import("@/pages/account"), "Support");
const SupportTicket = lazyNamed(
  () => import("@/pages/account"),
  "SupportTicket",
);
const Chat = lazyNamed(() => import("@/pages/account"), "Chat");
const Admin = lazyNamed(() => import("@/pages/staff"), "Admin");
const Master = lazyNamed(() => import("@/pages/staff"), "Master");
const About = lazyNamed(() => import("@/pages/legal"), "About");
const Terms = lazyNamed(() => import("@/pages/legal"), "Terms");
const Privacy = lazyNamed(() => import("@/pages/legal"), "Privacy");
const Contact = lazyNamed(() => import("@/pages/legal"), "Contact");
const NotFound = lazyNamed(() => import("@/pages/legal"), "NotFound");

function Loader() {
  return (
    <div className="full-loader">
      <div className="loader-g">G</div>
      <span>Loading secure module</span>
    </div>
  );
}

export default function App() {
  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="marketplace" element={<Marketplace />} />
          <Route path="search" element={<Marketplace searchMode />} />
          <Route path="categories" element={<Categories />} />
          <Route path="categories/:slug" element={<CategoryPage />} />
          <Route path="products/:slug" element={<ProductPage />} />
          <Route path="sellers/:username" element={<SellerPage />} />
          <Route path="login" element={<Login />} />
          <Route path="register" element={<Register />} />
          <Route path="staff-access" element={<StaffLogin />} />
          <Route path="about" element={<About />} />
          <Route path="terms" element={<Terms />} />
          <Route path="privacy" element={<Privacy />} />
          <Route path="contact" element={<Contact />} />
          <Route element={<Protected />}>
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="wallet" element={<Wallet />} />
            <Route path="purchases" element={<Purchases />} />
            <Route path="sales" element={<Sales />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="profile" element={<Profile />} />
            <Route path="settings" element={<Settings />} />
            <Route path="support" element={<Support />} />
            <Route path="support/:id" element={<SupportTicket />} />
            <Route path="chat" element={<Chat />} />
            <Route path="sell/new" element={<NewListing />} />
            <Route path="listings/:slug/edit" element={<NewListing />} />
            <Route path="payment-result" element={<PaymentResult />} />
          </Route>
          <Route element={<Protected roles={["admin", "master"]} />}>
            {" "}
            <Route path="admin" element={<Admin />} />
          </Route>
          <Route element={<Protected roles={["master"]} />}>
            {" "}
            <Route path="master" element={<Master />} />
          </Route>
          <Route path="404" element={<NotFound />} />
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
